/**
 * Coordinating hook for AssetOverview graph queries
 *
 * Consolidates 3 separate graph queries (parentAssets, ultimateOrigins, childAssets)
 * into a single interface while maintaining React Query's automatic parallelism.
 * These queries support the asset relationship graph and origination display.
 */

import { TreeNode } from '@/types';

import { useChildAssetsQuery } from './useChildAssetsQuery';
import { useParentAssetsQuery } from './useParentAssetsQuery';
import { useUltimateOriginsQuery } from './useUltimateOriginsQuery';

export interface UseAssetOverviewQueriesResult {
  /** Parent assets that discovered this asset (2-hop traversal) */
  parentAssets: TreeNode[] | undefined;
  /** Ultimate origination sources (recursive discovery chain) */
  ultimateOrigins: TreeNode[] | undefined;
  /** Child assets discovered by this asset (2-hop traversal) */
  childAssets: TreeNode[] | undefined;
  /** True if any query is in initial loading state */
  isLoading: boolean;
  /** Individual query statuses for granular control */
  statuses: {
    parentAssets: 'pending' | 'error' | 'success';
    ultimateOrigins: 'pending' | 'error' | 'success';
    childAssets: 'pending' | 'error' | 'success';
  };
}

/**
 * Custom hook for coordinating AssetOverview graph queries
 *
 * Executes 3 queries in parallel (all need assetKey):
 * 1. parentAssets - Assets that discovered this asset
 * 2. ultimateOrigins - Root sources in the discovery chain
 * 3. childAssets - Assets discovered by this asset
 *
 * All queries execute in parallel using React Query's automatic parallelism.
 *
 * @param assetKey - The asset key to fetch relationship data for
 * @returns Consolidated graph data with loading states
 */
export function useAssetOverviewQueries(
  assetKey: string
): UseAssetOverviewQueriesResult {
  // Query 1: Fetch parent assets (2-hop DISCOVERED/HAS_ATTRIBUTE traversal)
  const {
    data: parentAssets,
    isLoading: parentAssetsLoading,
    status: parentAssetsStatus,
  } = useParentAssetsQuery(assetKey);

  // Query 2: Fetch ultimate origins (recursive DISCOVERED chain)
  const {
    data: ultimateOrigins,
    isLoading: ultimateOriginsLoading,
    status: ultimateOriginsStatus,
  } = useUltimateOriginsQuery(assetKey);

  // Query 3: Fetch child assets (2-hop outbound relationships)
  const {
    data: childAssets,
    isLoading: childAssetsLoading,
    status: childAssetsStatus,
  } = useChildAssetsQuery(assetKey);

  // Calculate unified loading state
  const isLoading =
    parentAssetsLoading || ultimateOriginsLoading || childAssetsLoading;

  return {
    parentAssets,
    ultimateOrigins,
    childAssets,
    isLoading,
    statuses: {
      parentAssets: parentAssetsStatus,
      ultimateOrigins: ultimateOriginsStatus,
      childAssets: childAssetsStatus,
    },
  };
}
